import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import HomeTemplate from '../components/home/HomeTemplate';
import { getHomeRebuildConfigByVariant } from '../data/homePage';
import Seo from '../components/Seo';
import { homePageSchema } from '../utils/structuredData';
import { isPrerendering } from '../utils/prerendering';
import { useTheme } from '../contexts/ThemeContext';
import useLandingStylingParams from '../hooks/useLandingStylingParams';

/**
 * Marketing home at /home (JSON-driven HomeTemplate — Precision Desk).
 * Variant selected via ?v=tag. Legacy /landing redirects here.
 * Example: /home?v=a. Default: v=a (home-rebuild) when ?v= is omitted or invalid.
 *
 * Classic alternate shell: pages/HomeClassic.js. Older legacy: pages/HomeLegacy.js.
 */
const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { stylingAllowed, themeFromUrl } = useLandingStylingParams();
  const { switchTheme } = useTheme();
  const appliedThemeFromUrl = useRef(false);
  const variantTag = searchParams.get('v');
  const config = getHomeRebuildConfigByVariant(variantTag);
  const version = variantTag?.trim()?.toLowerCase() || 'a';

  useEffect(() => {
    if (isPrerendering()) return;
    try {
      sessionStorage.setItem('landing_page_version', version);
      sessionStorage.setItem('home_page_version', version);
    } catch (e) {
      /* ignore */
    }
  }, [version]);

  useEffect(() => {
    if (isPrerendering()) return;
    if (!searchParams.has('v')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('v', 'a');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isPrerendering() || appliedThemeFromUrl.current) return;
    if (stylingAllowed && themeFromUrl) {
      switchTheme(themeFromUrl);
      appliedThemeFromUrl.current = true;
    }
  }, [stylingAllowed, themeFromUrl, switchTheme]);

  return (
    <>
      <Seo
        title="PrecisionTrader — Stop Giving Back Your Profits"
        description="Your strategy isn't the problem. Consistent execution is. PrecisionTrader is a trading platform that enforces the rules you choose — in real time."
        canonicalPath="/home"
        schema={homePageSchema}
        schemaId="home-jsonld"
      />
      <HomeTemplate config={config} />
    </>
  );
};

export default Home;
